<template>
  <b-modal
    v-model="showModal"
    :title="`Employee Detail - ${companyName}`"
    size="xl"
    hide-footer
    modal-class="employee-detail-modal"
    @hide="closeModal"
  >
    <template #modal-header="{ close }">
      <h5 class="modal-title mb-0">Employee Detail - {{ companyName }}</h5>
      <button type="button" class="close" aria-label="Close" @click="close()">
        <span aria-hidden="true">&times;</span>
      </button>
    </template>
    <div class="employee-modal-content">
      <div class="download-wrapper mb-3">
        <span class="download-label">Download Report</span>
        <b-button variant="danger" :disabled="isExporting" @click="downloadReport">
          <i class="fe fe-download"></i>
          {{ isExporting ? 'Exporting...' : 'Report' }}
        </b-button>
      </div>

      <!-- Legend Section -->
      <div class="legend-section">
        <div class="row">
          <div class="col">
            <span class="legend-key">M</span> = Mandatory
          </div>
          <div class="col">
            <span class="legend-key">D</span> = Development
          </div>
          <div class="col">
            <span class="legend-key">C</span> = Completed
          </div>
          <div class="col">
            <span class="legend-key">NC</span> = Not Completed
          </div>
          <div class="col">
            <span class="legend-key">ED</span> = Expired
          </div>
        </div>
      </div>

      <!-- Table Section -->
      <div class="table-section">
        <!-- Additional Filters -->
        <div class="additional-filters mb-3">
          <div class="d-flex flex-wrap align-items-end gap-3">
            <!-- Division Filter -->
            <div
              v-if="isHROperationsSiteRole"
              class="flex-fill"
              style="min-width: 200px; max-width: 300px;"
            >
              <label class="mb-1 font-weight-bold">Division:</label>
              <v-select
                v-model="filters.division"
                :options="divisionOptions"
                placeholder="All Divisions"
                :clearable="true"
                :loading="isLoadingFilterOptions"
                :disabled="isLoadingFilterOptions"
                :filterable="true"
                class="filter-select"
              >
                <template #no-options="{ search }">
                  <span v-if="isLoadingFilterOptions">Loading divisions...</span>
                  <span v-else-if="search">No matching BIB division.</span>
                  <span v-else>No BIB division available.</span>
                </template>
              </v-select>
            </div>

            <!-- Department Filter -->
            <div
              v-if="isHROperationsSiteRole"
              class="flex-fill"
              style="min-width: 200px; max-width: 300px;"
            >
              <label class="mb-1 font-weight-bold">Department:</label>
              <v-select
                v-model="filters.department"
                :options="departmentOptions"
                placeholder="All Departments"
                :clearable="true"
                :loading="isLoadingFilterOptions"
                :disabled="isLoadingFilterOptions"
                :filterable="true"
                class="filter-select"
              >
                <template #no-options="{ search }">
                  <span v-if="isLoadingFilterOptions">Loading departments...</span>
                  <span v-else-if="search">No matching BIB department.</span>
                  <span v-else>No BIB department available.</span>
                </template>
              </v-select>
            </div>

            <!-- PLAN Filter -->
            <div class="flex-fill" style="min-width: 200px; max-width: 300px;">
              <label class="mb-1 font-weight-bold">PLAN:</label>
              <v-select
                v-model="filters.plan"
                :options="planOptions"
                label="label"
                :reduce="(option) => option.value"
                placeholder="All Plan"
                :clearable="true"
                class="filter-select"
              ></v-select>
            </div>

            <!-- ACTUAL Filter -->
            <div class="flex-fill" style="min-width: 200px; max-width: 300px;">
              <label class="mb-1 font-weight-bold">ACTUAL:</label>
              <v-select
                v-model="filters.actual"
                :options="actualOptions"
                label="label"
                :reduce="(option) => option.value"
                placeholder="All Actual"
                :clearable="true"
                class="filter-select"
              ></v-select>
            </div>

            <!-- Clear All -->
            <div class="d-flex" style="min-width: 140px;">
              <b-button 
                variant="outline-secondary" 
                size="sm" 
                class="flex-fill"
                @click="clearAllFilters"
              >
                Clear All
              </b-button>
            </div>
          </div>
        </div>

        <!-- Search by Name -->
        <div class="search-filter mb-3">
          <div class="row align-items-center">
            <div class="col-auto">
              <label class="mb-0 font-weight-bold">Search Employee:</label>
            </div>
            <div class="col-md-4">
              <b-form-input
                v-model="searchName"
                placeholder="Search by name..."
                @input="onSearchChange"
                class="search-input"
              ></b-form-input>
            </div>
          </div>
        </div>

        <!-- Competency Filter -->
        <div class="competency-filter mb-3">
          <div class="row align-items-center">
            <div class="col-auto">
              <label class="mb-0 font-weight-bold">Filter Course Category:</label>
            </div>
            <div class="col-md-4">
              <v-select
                v-model="selectedCompetenciesLocal"
                :options="competencyOptions"
                :reduce="option => option.code"
                label="label"
                placeholder="All Competencies"
                :clearable="true"
                class="competency-select"
                @input="onCompetencyFilterChange"
              >
                <template #option="{ code, label }">
                  <strong>{{ code }}</strong> - {{ label }}
                </template>
              </v-select>
            </div>
            <div class="col-auto" v-if="selectedCompetenciesLocal">
              <b-button variant="outline-primary" size="sm" @click="clearFilter">
                Clear Filter
              </b-button>
            </div>
          </div>
        </div>
        
        <div class="table-scroll-wrapper">
          <!-- Loading Indicator -->
          <div v-if="isLoading" class="text-center py-5">
            <b-spinner variant="primary"></b-spinner>
            <p class="mt-2">Loading employee data...</p>
          </div>

          <!-- Table -->
          <table v-else class="table employee-table">
          <thead>
            <tr>
              <th class="text-center" rowspan="3" style="vertical-align: middle;">NO</th>
              <th rowspan="3" style="vertical-align: middle;">NAME</th>
              <th rowspan="3" style="vertical-align: middle;">POSITION</th>
              <th v-if="filteredCompetencyTypes.length > 0" class="text-center" :colspan="filteredCompetencyTypes.length * 2">COURSES</th>
              <th class="text-center sortable" rowspan="3" style="vertical-align: middle; cursor: pointer;" @click="toggleSort">
                TOTAL 
                <i v-if="totalOrder === 'ASC'" class="fe fe-arrow-up"></i>
                <i v-else-if="totalOrder === 'DESC'" class="fe fe-arrow-down"></i>
                <i v-else class="fe fe-minus"></i>
              </th>
            </tr>
            <tr>
              <template v-for="competency in filteredCompetencyTypes">
                <th class="text-center" colspan="2" :key="competency.code">{{ competency.code }}</th>
              </template>
            </tr>
            <tr>
              <template v-for="competency in filteredCompetencyTypes">
                <th class="text-center" :key="competency.code + '-plan'">PLAN</th>
                <th class="text-center" :key="competency.code + '-actual'">ACTUAL</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!isLoading && displayedEmployeeList.length === 0">
              <td :colspan="3 + (filteredCompetencyTypes.length * 2) + 1" class="text-center py-4">
                No data available
              </td>
            </tr>
            <tr v-for="(employee, index) in displayedEmployeeList" :key="index">
              <td class="text-center">{{ (pagination.page - 1) * pagination.limit + index + 1 }}</td>
              <td>{{ employee.name }}</td>
              <td>{{ employee.position }}</td>
              <template v-for="competency in filteredCompetencyTypes">
                <td class="text-center" :key="competency.code + '-plan-' + index">{{ employee.competencies[competency.code]?.plan || '-' }}</td>
                <td class="text-center" :key="competency.code + '-actual-' + index">{{ employee.competencies[competency.code]?.actual || '-' }}</td>
              </template>
              <td class="text-center">{{ employee.total }}%</td>
            </tr>
          </tbody>
        </table>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper">
          <div class="d-flex justify-content-between align-items-center">
            <div class="pagination-info">
              Showing {{ (pagination.page - 1) * pagination.limit + 1 }} to {{ Math.min(pagination.page * pagination.limit, pagination.total) }} of {{ pagination.total }} entries
            </div>
            <nav>
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: pagination.page === 1 }">
                  <a class="page-link" href="#" @click.prevent="changePage(pagination.page - 1)">‹</a>
                </li>
                <li 
                  v-for="page in visiblePages" 
                  :key="page" 
                  class="page-item" 
                  :class="{ active: pagination.page === page, disabled: page === '...' }"
                >
                  <a 
                    v-if="page === '...'" 
                    class="page-link" 
                    href="#"
                    @click.prevent>
                    ...
                  </a>
                  <a 
                    v-else
                    class="page-link" 
                    href="#" 
                    @click.prevent="changePage(page)">
                    {{ page }}
                  </a>
                </li>
                <li class="page-item" :class="{ disabled: pagination.page === pagination.totalPages }">
                  <a class="page-link" href="#" @click.prevent="changePage(pagination.page + 1)">›</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import {
  GET_DASHBOARD_EMPLOYEES_PARTNER,
  GET_DASHBOARD_EMPLOYEES_HSE,
  GET_DASHBOARD_EMPLOYEES_SITE,
  GET_DASHBOARD_EMPLOYEES_INTERNAL,
  GET_DASHBOARD_COMPETENCIES_INTERNAL_EMPLOYEES,
  GET_DASHBOARD_COMPANY_EMPLOYEES
} from '@/api/dashboard/dashboard';
import { GET_LIST_USER_BY_FILTERS } from '@/api/users/users';
import moment from 'moment';
import {
  exportEmployeeMatrixExcel,
  buildDashboardReportFilename,
  formatReportPeriod,
  formatTrainingTypeLabel,
  formatActualFilterLabel
} from '@/utils/dashboardExcelExport';

const BIB_FILTER_PARAMS = {
  bibOnly: true,
  employeeLimit: 25
};

export default {
  name: "EmployeeDetailModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    companyName: {
      type: String,
      default: ""
    },
    companyData: {
      type: Object,
      default: null
    },
    selectedRole: {
      type: Object,
      default: null
    },
    dateRange: {
      type: Object,
      default: () => ({
        start: null,
        end: null
      })
    },
    selectedCompetencies: {
      type: String,
      default: null
    },
    isHROperationsSiteRole: {
      type: Boolean,
      default: false
    },
    isHSETrainingRole: {
      type: Boolean,
      default: false
    },
    isPartnerRole: {
      type: Boolean,
      default: false
    },
    isHROperationsJakartaRole: {
      type: Boolean,
      default: false
    },
    isEmployeeRole: {
      type: Boolean,
      default: false
    },
    isManagerRole: {
      type: Boolean,
      default: false
    },
    kplhType: {
      type: String,
      default: 'KPLH'
    },
    trainingType: {
      type: String,
      default: 'all'
    }
  },
  data() {
    return {
      isLoading: false,
      isExporting: false,
      isLoadingFilterOptions: false,
      suppressFilterWatch: false,
      selectedCompetenciesLocal: null,
      searchName: '',
      totalOrder: null, // null, 'ASC', or 'DESC'
      searchDebounceTimer: null,
      filterRefreshTimer: null,
      filters: {
        division: null,
        department: null,
        plan: null,
        actual: null,
        searchCourseName: ''
      },
      pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
      },
      divisionOptions: [],
      departmentOptions: [],
      initialDivisionOptions: [],
      initialDepartmentOptions: [],
      planOptions: [
        { label: 'Mandatory', value: 'mandatory' },
        { label: 'Development', value: 'development' }
      ],
      actualOptions: [
        { label: 'Completed', value: 'C' },
        { label: 'Not Completed', value: 'NC' },
        { label: 'Expired', value: 'ED' }
      ],
      // Employee data from API
      employeeList: []
    };
  },
  computed: {
    ...mapState('trainingCategories', ['trainingCategories']),
    effectiveTrainingType() {
      // Modal PLAN filter overrides dashboard Training Type when set
      return this.filters.plan || this.trainingType || 'all';
    },
    displayedEmployeeList() {
      if (!this.filters.actual) {
        return this.employeeList;
      }

      return this.employeeList.filter((employee) =>
        Object.values(employee.competencies || {}).some(
          (competency) => competency?.actual === this.filters.actual
        )
      );
    },
    competencyTypes() {
      return (Array.isArray(this.trainingCategories) ? this.trainingCategories : []).map(category => ({
        code: category.abbreviation,
        name: category.title,
        id: category.id
      }));
    },
    showModal: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      }
    },
    competencyOptions() {
      return this.competencyTypes.map(comp => ({
        code: comp.code,
        label: `${comp.code} - ${comp.name}`
      }));
    },
    filteredCompetencyTypes() {
      // Get all unique training names from employee data
      const allTrainings = new Set();
      this.employeeList.forEach(employee => {
        if (employee.competencies) {
          Object.keys(employee.competencies).forEach(trainingName => {
            allTrainings.add(trainingName);
          });
        }
      });

      // Map to training objects with their details
      const trainings = Array.from(allTrainings).map(trainingName => {
        // Get training_category_id from first employee's competency data
        const firstEmployee = this.employeeList.find(e => e.competencies && e.competencies[trainingName]);
        const training_category_id = firstEmployee?.competencies[trainingName]?.training_category_id;
        
        return {
          code: trainingName,
          name: trainingName,
          id: training_category_id
        };
      });

      return trainings;
    },
    visiblePages() {
      const pages = [];
      const total = this.pagination.totalPages;
      const current = this.pagination.page;
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        pages.push(total);
      }
      
      return pages.length > 0 ? pages : [1];
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetModalState();
        this.fetchTrainingCategoriesData();
        if (this.isHROperationsSiteRole) {
          this.fetchFilterOptions();
        }
        this.fetchEmployeeData();
      } else {
        this.clearPendingTimers();
      }
    },
    'filters.division'() {
      if (!this.visible || this.suppressFilterWatch) return;
      this.pagination.page = 1;
      this.scheduleEmployeeDataFetch();
    },
    'filters.department'() {
      if (!this.visible || this.suppressFilterWatch) return;
      this.pagination.page = 1;
      this.scheduleEmployeeDataFetch();
    },
    'filters.plan'() {
      if (!this.visible || this.suppressFilterWatch) return;
      this.pagination.page = 1;
      this.scheduleEmployeeDataFetch();
    },
    'filters.actual'() {
      if (!this.visible || this.suppressFilterWatch) return;
      this.pagination.page = 1;
      // Actual is applied client-side on the current result set
    },
    trainingType() {
      if (this.visible) {
        this.pagination.page = 1;
        this.scheduleEmployeeDataFetch();
      }
    }
  },
  mounted() {
    this.fetchTrainingCategoriesData();
  },
  beforeDestroy() {
    this.clearPendingTimers();
  },
  methods: {
    ...mapActions('trainingCategories', ['fetchTrainingCategories']),
    cleanParams(params = {}) {
      return Object.fromEntries(
        Object.entries(params || {}).filter(([, value]) => value !== null && value !== undefined && value !== '')
      );
    },
    clearPendingTimers() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
      if (this.filterRefreshTimer) {
        clearTimeout(this.filterRefreshTimer);
        this.filterRefreshTimer = null;
      }
    },
    normalizeFilterOptions(values = []) {
      return [...new Set(
        (Array.isArray(values) ? values : [])
          .map((item) => {
            if (item == null) return '';
            if (typeof item === 'string' || typeof item === 'number') {
              return String(item).trim();
            }
            return String(
              item.division ||
              item.department ||
              item.name ||
              item.label ||
              item.value ||
              ''
            ).trim();
          })
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b, 'id', { sensitivity: 'base' }));
    },
    resetModalState() {
      this.suppressFilterWatch = true;
      this.pagination.page = 1;
      this.searchName = '';
      this.totalOrder = 'DESC';
      this.selectedCompetenciesLocal = this.selectedCompetencies || null;
      this.filters.division = null;
      this.filters.department = null;
      this.filters.plan = null;
      this.filters.actual = null;
      this.filters.searchCourseName = '';
      this.$nextTick(() => {
        this.suppressFilterWatch = false;
      });
    },
    scheduleEmployeeDataFetch(delay = 200) {
      if (this.filterRefreshTimer) {
        clearTimeout(this.filterRefreshTimer);
      }
      this.filterRefreshTimer = setTimeout(() => {
        this.filterRefreshTimer = null;
        this.fetchEmployeeData();
      }, delay);
    },
    async fetchTrainingCategoriesData() {
      try {
        if (Array.isArray(this.trainingCategories) && this.trainingCategories.length > 0) return;
        await this.fetchTrainingCategories({ limit: '', search: '' });
      } catch (error) {
        console.error('Error fetching training categories:', error);
      }
    },
    async fetchFilterOptions() {
      if (this.isLoadingFilterOptions) return;

      // Reuse cached BIB options so reopening the modal stays responsive
      if (this.initialDivisionOptions.length || this.initialDepartmentOptions.length) {
        this.divisionOptions = [...this.initialDivisionOptions];
        this.departmentOptions = [...this.initialDepartmentOptions];
        this.pruneInvalidFilterSelections();
        return;
      }

      this.isLoadingFilterOptions = true;
      try {
        // Use BIB employee-filter master so options stay within BU BIB scope
        const response = await GET_LIST_USER_BY_FILTERS(this.cleanParams({
          ...BIB_FILTER_PARAMS,
          company: this.companyName || undefined
        }));
        const payload = response?.data?.data || {};

        this.initialDivisionOptions = this.normalizeFilterOptions(payload.division);
        this.initialDepartmentOptions = this.normalizeFilterOptions(payload.department);
        this.divisionOptions = [...this.initialDivisionOptions];
        this.departmentOptions = [...this.initialDepartmentOptions];

        this.pruneInvalidFilterSelections();
      } catch (error) {
        console.error('Error fetching dashboard employee filter options:', error);
        this.divisionOptions = [];
        this.departmentOptions = [];
        this.initialDivisionOptions = [];
        this.initialDepartmentOptions = [];
      } finally {
        this.isLoadingFilterOptions = false;
      }
    },
    pruneInvalidFilterSelections() {
      const hadInvalidDivision =
        this.filters.division && !this.divisionOptions.includes(this.filters.division);
      const hadInvalidDepartment =
        this.filters.department && !this.departmentOptions.includes(this.filters.department);

      if (!hadInvalidDivision && !hadInvalidDepartment) return;

      this.suppressFilterWatch = true;
      if (hadInvalidDivision) this.filters.division = null;
      if (hadInvalidDepartment) this.filters.department = null;
      this.$nextTick(() => {
        this.suppressFilterWatch = false;
      });
    },
    async fetchEmployeeData() {
      // Company employee list (legacy mitra table) only when there is no role competency id
      if ((this.isEmployeeRole || this.isManagerRole) && this.companyName && !this.selectedRole?.id) {
        await this.fetchCompanyEmployees();
        return;
      }

      if (!this.selectedRole?.id) {
        console.error('No role competency ID provided');
        return;
      }

      this.isLoading = true;
      this.employeeList = [];
      
      try {
        const params = {
          start_date: this.dateRange.start ? moment(this.dateRange.start).format('MM/DD/YYYY') : null,
          end_date: this.dateRange.end ? moment(this.dateRange.end).format('MM/DD/YYYY') : null,
          page: this.pagination.page,
          limit: this.pagination.limit,
          training_type: this.effectiveTrainingType
        };

        if (this.filters.actual) {
          params.actual = this.filters.actual;
        }

        // Add training_category_id filter if selected
        if (this.selectedCompetenciesLocal) {
          const selectedComp = this.competencyTypes.find(ct => ct.code === this.selectedCompetenciesLocal);
          if (selectedComp) {
            params.training_category_id = selectedComp.id;
          }
        }

        // Add search parameter
        if (this.searchName && this.searchName.trim()) {
          params.search = this.searchName.trim();
        }

        // Add sort parameter
        if (this.totalOrder) {
          params.total_order = this.totalOrder;
        }

        if (this.filters.division) {
          params.division = this.filters.division;
        }

        if (this.filters.department) {
          params.department = this.filters.department;
        }

        // Use appropriate API based on role
        let response;
        
        // GET /dashboard/competencies/internal/:id?company=BIB&training_type=
        if ((this.isHROperationsSiteRole || this.isHROperationsJakartaRole || this.isEmployeeRole || this.isManagerRole) && this.kplhType === 'NON_KPLH') {
          params.matrix_kplh = 'NON_KPLH';
          params.company = this.companyName;
          
          console.log('this.selectedRole', this.selectedRole);
          // Use the first competency ID from selectedCompetencies or 'ALL'
          const competencyId = this.selectedRole.id;
          
          response = await GET_DASHBOARD_COMPETENCIES_INTERNAL_EMPLOYEES(
            competencyId,
            params
          );
        } else if (this.isPartnerRole) {
          // Partner API for Admin Mitra and PJO
          response = await GET_DASHBOARD_EMPLOYEES_PARTNER(
            this.selectedRole.id,
            params
          );
        } else if (this.isHSETrainingRole || this.isEmployeeRole || this.isManagerRole) {
          params.company = this.companyName;

          response = await GET_DASHBOARD_EMPLOYEES_HSE(
            this.selectedRole.id,
            params
          );
        } else if (this.isHROperationsSiteRole) {
          // Add company parameter for SITE API
          params.company = this.companyName;
          
          response = await GET_DASHBOARD_EMPLOYEES_SITE(
            this.selectedRole.id,
            params
          );
        } else {
          // Internal API for other roles (HR Operations Jakarta, etc.)
          params.company = this.companyName;
          
          response = await GET_DASHBOARD_EMPLOYEES_INTERNAL(
            this.selectedRole.id,
            params
          );
        }

        if (response.data && response.data.data && response.data.data.data) {
          // Handle new nested structure: response.data.data.data
          const apiData = response.data.data;
          
          // Transform API response to match component structure
          this.employeeList = apiData.data.map(employee => {
            const competencies = {};

            // Transform trainings object to competencies structure
            if (employee.trainings) {
              Object.keys(employee.trainings).forEach(trainingName => {
                const training = employee.trainings[trainingName];

                competencies[trainingName] = {
                  plan: training.plan,
                  actual: training.actual,
                  training_category_id: training.training_category_id
                };
              });
            }

            return {
              employee_id: employee.employee_id,
              name: employee.name,
              company: employee.company || '',
              position: employee.position,
              division: employee.division || '',
              department: employee.department || '',
              competencies: competencies,
              total: parseFloat(employee.total) || 0
            };
          });

          // Update pagination from API
          if (apiData.pagination) {
            this.pagination = {
              page: apiData.pagination.page,
              limit: apiData.pagination.pageSize || apiData.pagination.limit,
              total: apiData.pagination.total,
              totalPages: apiData.pagination.totalPages
            };
          }
        }
        else {
          this.employeeList = [];
          this.pagination = {
            ...this.pagination,
            total: 0,
            totalPages: 0
          };
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
        this.$bvToast.toast('Data karyawan belum berhasil dimuat', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.isLoading = false;
      }
    },
    onSearchChange() {
      // Debounce search to avoid too many API calls
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      
      this.searchDebounceTimer = setTimeout(() => {
        this.pagination.page = 1; // Reset to first page
        this.fetchEmployeeData();
      }, 500);
    },
    onCompetencyFilterChange() {
      this.pagination.page = 1; // Reset to first page
      this.fetchEmployeeData();
    },
    toggleSort() {
      // Cycle through: null -> ASC -> DESC -> null
      if (this.totalOrder === null) {
        this.totalOrder = 'ASC';
      } else if (this.totalOrder === 'ASC') {
        this.totalOrder = 'DESC';
      } else {
        this.totalOrder = null;
      }
      
      this.pagination.page = 1; // Reset to first page
      this.fetchEmployeeData();
    },
    clearFilter() {
      this.selectedCompetenciesLocal = null;
      this.pagination.page = 1;
      this.fetchEmployeeData();
    },
    clearAllFilters() {
      this.suppressFilterWatch = true;
      this.filters = {
        division: null,
        department: null,
        plan: null,
        actual: null,
        searchCourseName: ''
      };
      this.selectedCompetenciesLocal = null;
      this.searchName = '';
      this.totalOrder = null;
      this.pagination.page = 1;
      // Restore full independent BIB option lists
      this.divisionOptions = [...this.initialDivisionOptions];
      this.departmentOptions = [...this.initialDepartmentOptions];
      this.$nextTick(() => {
        this.suppressFilterWatch = false;
        this.fetchEmployeeData();
      });
    },
    closeModal() {
      this.$emit("close");
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.page = page;
        this.fetchEmployeeData();
      }
    },
    courseCategoryFilterLabel() {
      if (!this.selectedCompetenciesLocal) {
        return 'All Competencies';
      }
      const option = this.competencyOptions.find((item) => item.code === this.selectedCompetenciesLocal);
      return option?.label || this.selectedCompetenciesLocal;
    },
    planFilterLabel() {
      return this.filters.plan ? formatTrainingTypeLabel(this.filters.plan) : 'All Plan';
    },
    buildEmployeeQueryParams(page, limit) {
      const params = {
        start_date: this.dateRange.start ? moment(this.dateRange.start).format('MM/DD/YYYY') : null,
        end_date: this.dateRange.end ? moment(this.dateRange.end).format('MM/DD/YYYY') : null,
        page,
        limit,
        training_type: this.effectiveTrainingType
      };

      if (this.filters.actual) {
        params.actual = this.filters.actual;
      }

      if (this.selectedCompetenciesLocal) {
        const selectedComp = this.competencyTypes.find((ct) => ct.code === this.selectedCompetenciesLocal);
        if (selectedComp) {
          params.training_category_id = selectedComp.id;
        }
      }

      if (this.searchName && this.searchName.trim()) {
        params.search = this.searchName.trim();
      }

      if (this.totalOrder) {
        params.total_order = this.totalOrder;
      }

      if (this.filters.division) {
        params.division = this.filters.division;
      }

      if (this.filters.department) {
        params.department = this.filters.department;
      }

      return params;
    },
    async requestEmployeeApi(params) {
      const requestParams = { ...params };

      if ((this.isEmployeeRole || this.isManagerRole) && this.companyName && !this.selectedRole?.id) {
        if (this.companyName) {
          requestParams.company = this.companyName;
        }
        return GET_DASHBOARD_COMPANY_EMPLOYEES(requestParams);
      }

      if (!this.selectedRole?.id) {
        throw new Error('No role competency ID provided');
      }

      if ((this.isHROperationsSiteRole || this.isHROperationsJakartaRole || this.isEmployeeRole || this.isManagerRole) && this.kplhType === 'NON_KPLH') {
        requestParams.matrix_kplh = 'NON_KPLH';
        requestParams.company = this.companyName;
        return GET_DASHBOARD_COMPETENCIES_INTERNAL_EMPLOYEES(this.selectedRole.id, requestParams);
      }

      if (this.isPartnerRole) {
        return GET_DASHBOARD_EMPLOYEES_PARTNER(this.selectedRole.id, requestParams);
      }

      if (this.isHSETrainingRole || this.isEmployeeRole || this.isManagerRole) {
        requestParams.company = this.companyName;
        return GET_DASHBOARD_EMPLOYEES_HSE(this.selectedRole.id, requestParams);
      }

      if (this.isHROperationsSiteRole) {
        requestParams.company = this.companyName;
        return GET_DASHBOARD_EMPLOYEES_SITE(this.selectedRole.id, requestParams);
      }

      requestParams.company = this.companyName;
      return GET_DASHBOARD_EMPLOYEES_INTERNAL(this.selectedRole.id, requestParams);
    },
    mapEmployeeRows(rows = []) {
      return (Array.isArray(rows) ? rows : []).map((employee) => {
        const competencies = {};

        if (employee.trainings) {
          Object.keys(employee.trainings).forEach((trainingName) => {
            const training = employee.trainings[trainingName];
            competencies[trainingName] = {
              plan: training.plan,
              actual: training.actual,
              training_category_id: training.training_category_id
            };
          });
        }

        return {
          employee_id: employee.employee_id,
          name: employee.name,
          company: employee.company || '',
          position: employee.position,
          division: employee.division || '',
          department: employee.department || '',
          competencies,
          total: parseFloat(employee.total) || 0
        };
      });
    },
    extractEmployeePayload(response) {
      const apiData = response?.data?.data;
      if (apiData?.data && Array.isArray(apiData.data)) {
        return {
          rows: this.mapEmployeeRows(apiData.data),
          pagination: apiData.pagination || null
        };
      }
      return { rows: [], pagination: null };
    },
    competencyTypesFromEmployees(employees = []) {
      const allTrainings = new Set();
      employees.forEach((employee) => {
        if (employee.competencies) {
          Object.keys(employee.competencies).forEach((trainingName) => {
            allTrainings.add(trainingName);
          });
        }
      });

      return Array.from(allTrainings).map((trainingName) => {
        const firstEmployee = employees.find((item) => item.competencies && item.competencies[trainingName]);
        return {
          code: trainingName,
          name: trainingName,
          id: firstEmployee?.competencies[trainingName]?.training_category_id
        };
      });
    },
    filterEmployeesByActual(employees = []) {
      if (!this.filters.actual) {
        return employees;
      }

      return employees.filter((employee) =>
        Object.values(employee.competencies || {}).some(
          (competency) => competency?.actual === this.filters.actual
        )
      );
    },
    async fetchAllEmployeesForExport() {
      const pageSize = Math.max(this.pagination.total || 0, this.employeeList.length, 100);
      const firstPayload = this.extractEmployeePayload(
        await this.requestEmployeeApi(this.buildEmployeeQueryParams(1, pageSize))
      );
      const all = [...firstPayload.rows];
      const totalPages = firstPayload.pagination?.totalPages || 1;

      for (let page = 2; page <= totalPages; page += 1) {
        const payload = this.extractEmployeePayload(
          await this.requestEmployeeApi(this.buildEmployeeQueryParams(page, pageSize))
        );
        all.push(...payload.rows);
      }

      return this.filterEmployeesByActual(all);
    },
    async downloadReport() {
      if (this.isExporting) return;

      this.isExporting = true;
      try {
        let employees = this.displayedEmployeeList || [];
        try {
          employees = await this.fetchAllEmployeesForExport();
        } catch (fetchError) {
          console.error('Error fetching all employees for export, using current table data:', fetchError);
          employees = this.displayedEmployeeList || [];
        }

        const courses = this.filteredCompetencyTypes;
        const companyLabel = this.companyName || 'All_company';
        const categoryLabel = this.selectedCompetenciesLocal || 'All';
        const metaRows = [
          { label: 'Report', value: 'Employee Detail' },
          { label: 'Company / Mitra', value: this.companyName || 'All Company' },
          { label: 'Role Competency', value: this.selectedRole?.title || '-' },
          { label: 'Plan', value: this.planFilterLabel() },
          { label: 'Actual', value: formatActualFilterLabel(this.filters.actual) },
          { label: 'Search Employee', value: this.searchName?.trim() || '-' },
          { label: 'Course Category', value: this.courseCategoryFilterLabel() },
          { label: 'Training Type', value: formatTrainingTypeLabel(this.effectiveTrainingType) },
          { label: 'Period', value: formatReportPeriod(this.dateRange) }
        ];

        if (this.isHROperationsSiteRole) {
          metaRows.splice(7, 0,
            { label: 'Division', value: this.filters.division || '-' },
            { label: 'Department', value: this.filters.department || '-' }
          );
        }

        await exportEmployeeMatrixExcel({
          sheetName: 'Employee Detail',
          filename: buildDashboardReportFilename(['Employee', companyLabel, categoryLabel]),
          metaRows,
          courses,
          employees
        });
        this.$bvToast.toast('Excel report downloaded', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Error exporting employee detail report:', error);
        this.$bvToast.toast('Failed to export Excel report', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.isExporting = false;
      }
    },
    async fetchCompanyEmployees() {
      if (!this.companyName) {
        this.employeeList = [];
        this.pagination = {
          page: 1,
          limit: this.pagination.limit,
          total: 0,
          totalPages: 0
        };
        return;
      }

      this.isLoading = true;
      this.employeeList = [];
      
      try {
        const params = {
          start_date: this.dateRange.start ? moment(this.dateRange.start).format('MM/DD/YYYY') : null,
          end_date: this.dateRange.end ? moment(this.dateRange.end).format('MM/DD/YYYY') : null,
          page: this.pagination.page,
          limit: this.pagination.limit,
          training_type: this.effectiveTrainingType
        };

        if (this.companyName) {
          params.company = this.companyName;
        }

        if (this.filters.actual) {
          params.actual = this.filters.actual;
        }

        // Add training_category_id filter if selected
        if (this.selectedCompetenciesLocal) {
          const selectedComp = this.competencyTypes.find(ct => ct.code === this.selectedCompetenciesLocal);
          if (selectedComp) {
            params.training_category_id = selectedComp.id;
          }
        }

        // Add search parameter
        if (this.searchName && this.searchName.trim()) {
          params.search = this.searchName.trim();
        }

        // Add sort parameter
        if (this.totalOrder) {
          params.total_order = this.totalOrder;
        }

        if (this.filters.division) {
          params.division = this.filters.division;
        }

        if (this.filters.department) {
          params.department = this.filters.department;
        }

        const response = await GET_DASHBOARD_COMPANY_EMPLOYEES(params);

        if (response.data.httpCode === 200) {
          const apiData = response.data.data;
          
          // Transform API response to match component structure
          this.employeeList = apiData.data.map(employee => {
            const competencies = {};

            // Transform trainings object to competencies structure
            if (employee.trainings) {
              Object.keys(employee.trainings).forEach(trainingName => {
                const training = employee.trainings[trainingName];

                competencies[trainingName] = {
                  plan: training.plan,
                  actual: training.actual,
                  training_category_id: training.training_category_id
                };
              });
            }

            return {
              employee_id: employee.employee_id,
              name: employee.name,
              company: employee.company || '',
              position: employee.position,
              division: employee.division || '',
              department: employee.department || '',
              competencies: competencies,
              total: parseFloat(employee.total) || 0
            };
          });

          // Update pagination from API
          this.pagination = {
            page: apiData.pagination.page,
            limit: apiData.pagination.limit,
            total: apiData.pagination.total,
            totalPages: apiData.pagination.totalPages
          };
        } else {
          this.employeeList = [];
          this.pagination = {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0
          };
        }
      } catch (error) {
        console.error('Error fetching company employees:', error);
        this.employeeList = [];
        this.pagination = {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        };
        
        this.$bvToast.toast('Data karyawan belum berhasil dimuat', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.employee-detail-modal >>> .modal-dialog {
  max-width: 90%;
}

.employee-detail-modal >>> .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.employee-modal-content {
  padding: 1.5rem;
}

.download-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 3;
  background: #fff;
  padding-bottom: 0.5rem;
}

.download-label {
  font-size: 1rem;
  font-weight: 700;
  color: #000;
}

/* Legend Section */
.legend-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.legend-section .row {
  font-size: 0.875rem;
}

.legend-key {
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: #B71C1C;
  color: #fff;
  font-weight: 700;
  border-radius: 3px;
  margin-right: 0.5rem;
}

/* Table Section */
.table-section {
  margin-top: 1.5rem;
}

/* Search Filter */
.search-filter {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.search-filter label {
  font-size: 0.875rem;
  color: #333;
}

.search-input {
  font-size: 0.875rem;
}

/* Competency Filter */
.competency-filter {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.competency-filter label {
  font-size: 0.875rem;
  color: #333;
}

.competency-select {
  font-size: 0.875rem;
}

.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-scroll-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.employee-table {
  width: 100%;
  margin-bottom: 1rem;
  font-size: 0.75rem;
  border-collapse: collapse;
}

.employee-table thead th {
  background-color: #fafafa;
  color: #9e9e9e;
  font-weight: 400;
  text-transform: uppercase;
  padding: 0.75rem 0.5rem;
  border: 1px solid #e0e0e0;
  font-size: 0.65rem;
  letter-spacing: 0.3px;
}

.employee-table thead th.sortable {
  cursor: pointer;
  user-select: none;
}

.employee-table thead th.sortable:hover {
  background-color: #f0f0f0;
}

.employee-table thead th.sortable i {
  font-size: 0.75rem;
  margin-left: 0.25rem;
}

.employee-table tbody td {
  padding: 1rem 0.5rem;
  border: 1px solid #e0e0e0;
  color: #000;
  background-color: #fff;
  font-size: 0.75rem;
}

.employee-table tbody tr:hover {
  background-color: #f8f9fa;
}

/* Pagination */
.pagination-wrapper {
  margin-top: 1rem;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6c757d;
}

.pagination {
  display: flex;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.page-item {
  margin: 0;
}

.page-link {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  color: #333;
  background-color: #fff;
  border: 1px solid #dee2e6;
  text-decoration: none;
  margin: 0 2px;
  border-radius: 3px;
}

.page-item.active .page-link {
  background-color: #B71C1C;
  border-color: #B71C1C;
  color: #fff;
}

.page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  background-color: #fff;
  border-color: #dee2e6;
}

.page-link:hover {
  color: #B71C1C;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.page-item.active .page-link:hover {
  color: #fff;
  background-color: #B71C1C;
}
</style>
